import React, { useEffect, useState } from "react";
import { useFormik } from "formik"; // ✅ Added Formik
import Modal from "@mui/material/Modal";
import {
  Box,
  Input,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DriveFolderUploadIcon from "@mui/icons-material/DriveFolderUpload";
import { UploadMedia, Uploadpdf, UploadVideo } from "../utils/mediaUpload";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

import {
  CONTENT_TYPE,
  POST_TYPE,
  TYPE_SUBJECT_LISTING,
} from "../constants/enums";
import {
  useCreateContentMutation,
  useUpdateContentMutation,
} from "../service/content";
import { showToast, showWarning } from "../constants/toast";
import CancelIcon from "@mui/icons-material/Cancel";
import AdvanceDocumentViewer from "../components/DocViewer";
import { useLazyGetSubjectsAndCategoryQuery } from "../service/auth";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};

interface AddFormProps {
  open: boolean;
  handleOpen: () => void;
  handleClose: () => void;
  data?: any;
  edit?: boolean;
  type: number;
}

export const AddPostModal = ({
  open,
  handleOpen,
  handleClose,
  data,
  edit,
  type,
}: AddFormProps) => {
  const [createContent] = useCreateContentMutation();
  const [updateContent] = useUpdateContentMutation();
  const [numPages, setNumPages] = useState<number>(0);
  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };
  const [getSubjects] = useLazyGetSubjectsAndCategoryQuery();
  const [subjects, setSubjects] = useState<any[]>([]);
  console.log(subjects, 'subjects');


  // ✅ Initialized Formik
  const formik = useFormik({
    initialValues: {
      title: data?.title || "",
      subject: data?.subjects?.map((s: any) => s._id) || [],
      category: data?.category?._id || "1",
      description: data?.description || "",
      images: data?.images ? data?.images : ([] as string[]),
    },

    onSubmit: async (values) => {
      if (values.images?.length == 0) {
        showWarning("Please add something in your post")
        return;
      }
      handleClose();
      const body = {
        visibility: Number(values.category),
        ...(values.title ? { topic: values.title } : {}),
        subjectId: values.subject,
        images: values.images,
        contentType: CONTENT_TYPE.POST,
      };
      console.log(body,'body');
      
      try {
        let res;
        if (edit) {
          res = await updateContent({ id: data._id, body: body }).unwrap();
        } else {
          res = await createContent(body).unwrap();
        }
        if (res?.statusCode === 200) {
          showToast("Post added successfully");
          formik.resetForm();
        }
      } catch (error: any) {
        console.log(error);
      } finally {
        handleClose();
      }
    },
  });

  // ✅ Handle image upload and limit to 4 files
  //   const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
  //     const files = e.target.files;
  //     if (!files) return;

  //     const fileArray = Array.from(files).slice(0, 4); // Max 4
  //     try {
  //       const uploaded = await Promise.all(
  //         fileArray.map((file) => UploadMedia(file))
  //       );
  //       const uploadedUrls = uploaded.map((res) => res.data?.image); // adjust based on UploadMedia response shape
  //       formik.setFieldValue("images", [
  //         ...formik.values.images,
  //         ...uploadedUrls,
  //       ]);
  //     } catch (error) {
  //       console.error("Image upload failed:", error);
  //     }
  //   };


  const fetchSubjects = async () => {
    try {
      const res = await getSubjects({ type: TYPE_SUBJECT_LISTING.SUBJECT }).unwrap();
      if (res?.statusCode == 200) {
        setSubjects(res?.data)
      }
    } catch (error: any) {

    }
  }

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newFiles = Array.from(files);
    const currentFiles = formik.values.images || [];

    const imageFiles = newFiles.filter((file) =>
      file.type.startsWith("image/")
    );
    const videoFiles = newFiles.filter((file) =>
      file.type.startsWith("video/")
    );
    const pdfFiles = newFiles.filter((file) => file.type === "application/pdf");

    const uploads: string[] = [];

    try {
      // Handle image upload (max 4 images total)
      const imageLimit = 4 - currentFiles.length;
      const limitedImages = imageFiles.slice(0, imageLimit);

      const uploadedImages = await Promise.all(
        limitedImages.map((file) => UploadMedia(file))
      );
      uploads.push(...uploadedImages.map((res) => res.data?.image));

      // Handle single video upload (if no video already uploaded)
      const hasVideo = currentFiles.some(
        (file: any) =>
          file?.includes(".mp4") ||
          file?.includes(".mov") ||
          file?.includes(".avi")
      );
      if (!hasVideo && videoFiles.length > 0) {
        const uploadedVideo = await UploadVideo(videoFiles[0]);
        uploads.push(uploadedVideo.data?.image);
      }

      // Handle single PDF upload (if no PDF already uploaded)
      const hasPdf = currentFiles.some((file: any) => file?.includes(".pdf"));
      if (!hasPdf && pdfFiles.length > 0) {
        const uploadedPdf = await Uploadpdf(pdfFiles[0]);
        uploads.push(uploadedPdf.data?.image);
      }

      formik.setFieldValue("images", [...currentFiles, ...uploads]);
    } catch (error) {
      console.error("Upload failed:", error);
    }
  };
  console.log(formik.values.images, "images");

  const handleRemoveImage = (indexToRemove: number) => {
    const updatedImages = formik.values.images.filter(
      (_: any, idx: any) => idx !== indexToRemove
    );
    formik.setFieldValue("images", updatedImages);
  };

  useEffect(() => {
    if (!open) {
      formik.resetForm();
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      fetchSubjects()
    }
  }, [open])

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style}>
        <div className="form_discuss add_post">
          <Typography id="modal-modal-title" variant="h2" component="h2">
            Add Post
          </Typography>
          <div className="add_post_drop">
            <figure>
              <img src="/static/images/user_add.png" alt="image" />
            </figure>

            <div className="control_group" >
              <Select
                name="category"
                value={formik.values.category}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                fullWidth
                displayEmpty
              >
                <MenuItem value="1">Anyone</MenuItem>
                <MenuItem value="2">Followers</MenuItem>
              </Select>
            </div>
          </div>



          <form
            className="form"
            onSubmit={formik.handleSubmit}
            style={{ marginTop: "20px" }}
          >
            <div className="control_group" style={{ marginTop: "15px" }}>
              <Select
                name="subject"
                multiple
                value={formik.values.subject}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                fullWidth
                disabled={!formik.values.category}
                displayEmpty
                renderValue={(selected: any) =>
                  subjects
                    .filter((s) => selected.includes(s._id))
                    .map((s) => s.name)
                    .join(", ") || "Select Subject"
                }
              >
                {subjects?.map((item) => (
                  <MenuItem key={item?._id} value={item?._id}>
                    {item?.name || "-"}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.subject && formik.errors.subject && (
                <Typography color="error" fontSize={12}>
                  {formik.errors.subject as string}
                </Typography>
              )}
            </div>
            <div className="control_group" style={{ marginTop: "15px" }}>
              <TextField
                fullWidth
                multiline
                minRows={6}
                maxRows={6}
                placeholder="Share your thoughts..."
                name="title"
                value={formik.values.title}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />

              {formik.touched.title && formik.errors.title && (
                <Typography color="error" fontSize={12}>
                  {formik.errors.title as string}
                </Typography>
              )}
            </div>

            <div className="ovr_flow">
              {formik.values.images &&
                formik.values.images.map((img: any, index: number) => (
                  <div className="upload_file">
                    {type === POST_TYPE.VIDEO ? (
                      <video
                        src={img || `Not available`}
                        controls={true}
                        autoPlay={false}
                      />
                    ) : type === POST_TYPE.DOCUMENT ? (


                      // <figure key={index}>
                      //   <img
                      //     onClick={() => window.open(img)}
                      //     src={`/static/images/pdf_icon.svg`}
                      //     alt="emili"
                      //   />
                      //   <figcaption
                      //     onClick={() => formik.setFieldValue("images", [])}
                      //     style={{ cursor: "pointer" }}
                      //   >
                      //     <CancelIcon />
                      //   </figcaption>
                      // </figure>
                      <AdvanceDocumentViewer url={img} title="pdf" />


                    ) : (
                      <figure key={index}>
                        <img
                          src={img || `/static/images/emili.png`}
                          alt="emili"
                        />
                        <figcaption
                          onClick={() => handleRemoveImage(index)}
                          style={{ cursor: "pointer" }}
                        >
                          <CancelIcon />
                        </figcaption>
                      </figure>
                    )}
                  </div>
                ))}
            </div>
            {((type === POST_TYPE.IMAGE && formik.values.images.length < 4) ||
              ((type === POST_TYPE.VIDEO ||
                type === POST_TYPE.DOCUMENT ||
                type === POST_TYPE.POLL) &&
                formik.values.images.length === 0)) && (
                <div className="upload_file">
                  <input
                    type="file"
                    multiple
                    accept={
                      type === POST_TYPE.VIDEO
                        ? "video/*"
                        : type === POST_TYPE.DOCUMENT
                          ? "application/pdf"
                          : "image/*"
                    }
                    onChange={handleImageChange}
                  />
                  <DriveFolderUploadIcon />
                  <p>Upload</p>


                  {formik.errors.images &&
                    typeof formik.errors.images === "string" && (
                      <Typography color="error" fontSize={12}>
                        {formik.errors.images}
                      </Typography>
                    )}
                </div>
              )}


            <div className="form_btn">
              <button type="submit" className=" btn primary">
                {edit ? "Save" : "Post"}
              </button>
            </div>
          </form>
        </div>
      </Box>
    </Modal>
  );
};
